/**
 * @fileoverview Main application router configuration with authentication-based route protection
 *
 * This file defines the complete routing structure for the application, implementing:
 * - Public routes that redirect authenticated users to dashboard
 * - Protected routes that require authentication
 * - Route guards using ProtectedRoute and PublicRoute components
 * - Default redirects for root and unknown paths
 *
 * Route Architecture:
 * - Examples routes (/examples/*) - Public showcase pages
 * - Authentication route (/signup) - Public auth page
 * - Dashboard route (/dashboard) - Protected application page
 * - Callback route (/callback) - OIDC login handler
 * - Catch-all redirects to examples for demonstration purposes
 *
 * Key Patterns:
 * - Uses React Router v7 with BrowserRouter for client-side routing
 * - Implements route protection using nested routes and <Outlet /> support
 * - Groups public and protected routes under route guards for maintainability
 * - Provides default navigation for root and unknown paths
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { AdminRoute } from '@/components/auth/admin-route';
import { PermissionGuard } from '@/components/auth/permission-guard';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { AdminLayout, ProtectedLayout } from '@/components/layout';
import { LoadingScreen } from '@/components/loading-screen';
import { PermissionsType } from '@/lib/data';
import { IndexRoute } from '@/router/index-route';

// FMICE-812 W10: every page is its own chunk, loaded on first visit behind the
// single Suspense fallback below (inside the guards, which stay eager).
const ActionItemCreate = lazy(
  () => import('@/pages/action-items/action-item-create'),
);
const ActionItemDetails = lazy(
  () => import('@/pages/action-items/action-item-details'),
);
const ActionItemList = lazy(
  () => import('@/pages/action-items/action-item-list'),
);
const AppConfigurationPage = lazy(() => import('@/pages/app-configuration'));
const AppNotificationCreateNew = lazy(
  () => import('@/pages/app-notifications/app-notification-create-new'),
);
const AppNotificationDetails = lazy(
  () => import('@/pages/app-notifications/app-notification-details'),
);
const AppNotificationList = lazy(
  () => import('@/pages/app-notifications/app-notification-list'),
);
const Callback = lazy(() =>
  import('@/pages/auth/callback').then((m) => ({ default: m.Callback })),
);
const SetupAccount = lazy(() =>
  import('@/pages/auth/setup-account').then((m) => ({ default: m.SetupAccount })),
);
const SignOut = lazy(() =>
  import('@/pages/auth/sign-out').then((m) => ({ default: m.SignOut })),
);
const SignOutSuccessPage = lazy(() => import('@/pages/auth/sign-out-success'));
const BlogCreateNew = lazy(() => import('@/pages/blogs/blog-create-new'));
const BlogEdit = lazy(() => import('@/pages/blogs/blog-edit'));
const BlogList = lazy(() => import('@/pages/blogs/blog-list'));
const CalendarPage = lazy(() => import('@/pages/calendar/calendar'));
const ClaimCreateNew = lazy(() => import('@/pages/claims/claim-create-new'));
const ClaimDetails = lazy(() => import('@/pages/claims/claim-details'));
const ClaimEditNew = lazy(() => import('@/pages/claims/claim-edit-new'));
const ClaimList = lazy(() => import('@/pages/claims/claim-list'));
const CoffeeConsolePage = lazy(
  () => import('@/pages/coffee-console/coffee-console-page'),
);
const CustomerDetails = lazy(
  () => import('@/pages/customers/customer-details'),
);
const CustomerList = lazy(() => import('@/pages/customers/customer-list'));
const EventCreateNew = lazy(() => import('@/pages/events/event-create-new'));
const EventDetails = lazy(() => import('@/pages/events/event-details'));
const EventList = lazy(() => import('@/pages/events/event-list'));
const FabricatorMembersList = lazy(
  () => import('@/pages/fabricator-members/fabricator-members-list'),
);
const FabricatorDetails = lazy(
  () => import('@/pages/fabricators/fabricator-details'),
);
const FabricatorList = lazy(
  () => import('@/pages/fabricators/fabricator-list'),
);
const FMIEnquiryDetails = lazy(
  () => import('@/pages/fmi-enquiries/fmi-enquiry-details'),
);
const FMIEnquiryList = lazy(
  () => import('@/pages/fmi-enquiries/fmi-enquiry-list'),
);
const FMILeadDetails = lazy(() => import('@/pages/fmi-leads/fmi-lead-details'));
const FMILeadList = lazy(() => import('@/pages/fmi-leads/fmi-lead-list'));
const FmiUserDetails = lazy(() => import('@/pages/fmi-users/fmi-user-details'));
const FmiUserList = lazy(() => import('@/pages/fmi-users/fmi-user-list'));
const ForbiddenPage = lazy(() => import('@/pages/forbidden'));
const FyiItemCreate = lazy(() => import('@/pages/fyi-items/fyi-item-create'));
const FyiItemDetails = lazy(() => import('@/pages/fyi-items/fyi-item-details'));
const FyiItemList = lazy(() => import('@/pages/fyi-items/fyi-item-list'));
const GlazierDetails = lazy(() => import('@/pages/glaziers/glazier-details'));
const GlazierList = lazy(() => import('@/pages/glaziers/glazier-list'));
const HistoricalClaimCreate = lazy(
  () => import('@/pages/historical-claims/historical-claim-create'),
);
const HistoricalClaimEdit = lazy(
  () => import('@/pages/historical-claims/historical-claim-edit'),
);
const HistoricalClaimList = lazy(
  () => import('@/pages/historical-claims/historical-claim-list'),
);
const ImpersonationPage = lazy(() => import('@/pages/impersonation'));
const InvoiceList = lazy(() => import('@/pages/invoices/invoice-list'));
const JobDetails = lazy(() => import('@/pages/jobs/job-details'));
const JobList = lazy(() => import('@/pages/jobs/job-list'));
const LeadDetails = lazy(() => import('@/pages/leads/lead-details'));
const LeadList = lazy(() => import('@/pages/leads/lead-list'));
const MeetingDetails = lazy(() => import('@/pages/meetings/meeting-details'));
const MeetingList = lazy(() => import('@/pages/meetings/meeting-list'));
const MeetingNote = lazy(() => import('@/pages/meetings/meeting-note'));
const NotFoundPage = lazy(() => import('@/pages/not-found'));
const ResourceCreatePage = lazy(() => import('@/pages/resource-create'));
const RoleDetails = lazy(() => import('@/pages/roles/role-details'));
const RoleTypeDetails = lazy(() => import('@/pages/roles/role-type-details'));
const RoleTypeList = lazy(() => import('@/pages/roles/role-type-list'));
const ChangePassword = lazy(() => import('@/pages/settings/change-password'));
const Settings = lazy(() => import('@/pages/settings/settings'));
const SupplierDetails = lazy(
  () => import('@/pages/suppliers/supplier-details'),
);
const SupplierList = lazy(() => import('@/pages/suppliers/supplier-list'));

/**
 * Main application router component implementing authentication-based route protection
 *
 * This component serves as the central routing configuration for the entire application.
 * It demonstrates best practices for:
 * - Route organization and grouping
 * - Authentication-based access control
 * - Proper use of route guards
 * - Default navigation patterns
 *
 * Route Groups:
 * 1. **Public Routes**: Accessible without authentication, redirect authenticated users
 *    - Examples showcase pages for demonstrating features
 *    - Signup page
 *
 * 2. **Protected Routes**: Require authentication, redirect to signup if not authenticated
 *    - Dashboard and application pages
 *
 * 3. **Other Routes**:
 *    - Callback (/callback): Handles OIDC login redirect
 *    - Root (/) and unknown paths (*) redirect to examples
 *
 * @returns React Router configuration with all application routes
 *
 * @example
 * ```tsx
 * // Usage in main App component
 * function App() {
 *   return (
 *     <AuthProvider>
 *       <AppRouter />
 *     </AuthProvider>
 *   )
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Adding a new protected route
 * <Route element={<ProtectedRoute />}>
 *   <Route path="/new-feature" element={<NewFeaturePage />} />
 * </Route>
 * ```
 */
export const AppRouter = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Auth callback route */}
          <Route path='/callback' element={<Callback />} />
          <Route path='/setup-account' element={<SetupAccount />} />
          <Route path='/sign-out' element={<SignOut />} />
          <Route path='/sign-out/success' element={<SignOutSuccessPage />} />

          <Route element={<ProtectedRoute />}>
            <Route element={<ProtectedLayout />}>
              {/* RF-W10: `HomePage` in the real build; a mock-gated redirect to
                  the coffee console under `--mode mock` (see index-route.tsx). */}
              <Route path='/' element={<IndexRoute />} />
              <Route
                path='/fmi-leads'
                element={
                  <PermissionGuard>
                    <FMILeadList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/fmi-leads/:id'
                element={
                  <PermissionGuard>
                    <FMILeadDetails />
                  </PermissionGuard>
                }
              />
              <Route
                path='/fmi-enquiries'
                element={
                  <PermissionGuard>
                    <FMIEnquiryList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/fmi-enquiries/:id'
                element={
                  <PermissionGuard>
                    <FMIEnquiryDetails />
                  </PermissionGuard>
                }
              />
              <Route
                path='/leads'
                element={
                  <PermissionGuard>
                    <LeadList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/leads/:id'
                element={
                  <PermissionGuard>
                    <LeadDetails />
                  </PermissionGuard>
                }
              />
              <Route
                path='/calendar'
                element={
                  <PermissionGuard>
                    <CalendarPage />
                  </PermissionGuard>
                }
              />
              <Route
                path='/meetings'
                element={
                  <PermissionGuard>
                    <MeetingList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/meetings/:id'
                element={
                  <PermissionGuard>
                    <MeetingDetails />
                  </PermissionGuard>
                }
              />
              <Route
                path='/meetings/:id/note'
                element={
                  <PermissionGuard>
                    <MeetingNote />
                  </PermissionGuard>
                }
              />
              <Route
                path='/action-items'
                element={
                  <PermissionGuard>
                    <ActionItemList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/action-items/create'
                element={
                  <PermissionGuard required={[PermissionsType.create]}>
                    <ActionItemCreate />
                  </PermissionGuard>
                }
              />
              <Route
                path='/action-items/:id'
                element={
                  <PermissionGuard>
                    <ActionItemDetails />
                  </PermissionGuard>
                }
              />
              <Route
                path='/fyi-items'
                element={
                  <PermissionGuard>
                    <FyiItemList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/fyi-items/create'
                element={
                  <PermissionGuard required={[PermissionsType.create]}>
                    <FyiItemCreate />
                  </PermissionGuard>
                }
              />
              <Route
                path='/fyi-items/:id'
                element={
                  <PermissionGuard>
                    <FyiItemDetails />
                  </PermissionGuard>
                }
              />
              <Route
                path='/claims'
                element={
                  <PermissionGuard>
                    <ClaimList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/claims/create'
                element={
                  <PermissionGuard required={[PermissionsType.create]}>
                    <ClaimCreateNew />
                  </PermissionGuard>
                }
              />
              <Route
                path='/claims/create/:id'
                element={
                  <PermissionGuard required={[PermissionsType.create]}>
                    <ClaimEditNew />
                  </PermissionGuard>
                }
              />
              <Route
                path='/claims/:id'
                element={
                  <PermissionGuard>
                    <ClaimDetails />
                  </PermissionGuard>
                }
              />
              <Route
                path='/historical-claims'
                element={
                  <PermissionGuard>
                    <HistoricalClaimList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/historical-claims/create'
                element={
                  <PermissionGuard required={[PermissionsType.create]}>
                    <HistoricalClaimCreate />
                  </PermissionGuard>
                }
              />
              <Route
                path='/historical-claims/:id/edit'
                element={
                  <PermissionGuard required={[PermissionsType.update]}>
                    <HistoricalClaimEdit />
                  </PermissionGuard>
                }
              />
              <Route
                path='/jobs'
                element={
                  <PermissionGuard>
                    <JobList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/jobs/:id'
                element={
                  <PermissionGuard>
                    <JobDetails />
                  </PermissionGuard>
                }
              />
              <Route
                path='/glazier-invoices'
                element={
                  <PermissionGuard>
                    <InvoiceList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/customers'
                element={
                  <PermissionGuard>
                    <CustomerList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/customers/:id'
                element={
                  <PermissionGuard>
                    <CustomerDetails />
                  </PermissionGuard>
                }
              />
              <Route
                path='/fabricators'
                element={
                  <PermissionGuard>
                    <FabricatorList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/fabricators/:id'
                element={
                  <PermissionGuard>
                    <FabricatorDetails />
                  </PermissionGuard>
                }
              />
              <Route
                path='/fabricator-members'
                element={
                  <PermissionGuard>
                    <FabricatorMembersList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/glaziers'
                element={
                  <PermissionGuard>
                    <GlazierList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/glaziers/:id'
                element={
                  <PermissionGuard>
                    <GlazierDetails />
                  </PermissionGuard>
                }
              />
              <Route
                path='/fmi-users'
                element={
                  <PermissionGuard>
                    <FmiUserList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/fmi-users/:id'
                element={
                  <PermissionGuard>
                    <FmiUserDetails />
                  </PermissionGuard>
                }
              />
              <Route
                path='/suppliers'
                element={
                  <PermissionGuard>
                    <SupplierList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/suppliers/:id'
                element={
                  <PermissionGuard>
                    <SupplierDetails />
                  </PermissionGuard>
                }
              />
              <Route
                path='/events'
                element={
                  <PermissionGuard>
                    <EventList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/events/create'
                element={
                  <PermissionGuard required={[PermissionsType.create]}>
                    <EventCreateNew />
                  </PermissionGuard>
                }
              />
              <Route
                path='/events/:id'
                element={
                  <PermissionGuard>
                    <EventDetails />
                  </PermissionGuard>
                }
              />
              <Route
                path='/app-notifications'
                element={
                  <PermissionGuard>
                    <AppNotificationList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/app-notifications/create'
                element={
                  <PermissionGuard required={[PermissionsType.create]}>
                    <AppNotificationCreateNew />
                  </PermissionGuard>
                }
              />
              <Route
                path='/app-notifications/:id'
                element={
                  <PermissionGuard>
                    <AppNotificationDetails />
                  </PermissionGuard>
                }
              />
              <Route
                path='/blogs'
                element={
                  <PermissionGuard>
                    <BlogList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/blogs/create'
                element={
                  <PermissionGuard required={[PermissionsType.create]}>
                    <BlogCreateNew />
                  </PermissionGuard>
                }
              />
              <Route
                path='/blogs/:id'
                element={
                  <PermissionGuard>
                    <BlogEdit />
                  </PermissionGuard>
                }
              />
              <Route
                path='/roles'
                element={
                  <PermissionGuard>
                    <RoleTypeList />
                  </PermissionGuard>
                }
              />
              <Route
                path='/roles/:roleType'
                element={
                  <PermissionGuard>
                    <RoleTypeDetails />
                  </PermissionGuard>
                }
              />
              <Route
                path='/roles/:roleType/:roleId'
                element={
                  <PermissionGuard>
                    <RoleDetails />
                  </PermissionGuard>
                }
              />
              <Route
                path='/coffee-console'
                element={
                  <PermissionGuard>
                    <CoffeeConsolePage />
                  </PermissionGuard>
                }
              />
              <Route path='/settings' element={<Settings />} />
              <Route
                path='/settings/change-password'
                element={<ChangePassword />}
              />
            </Route>
          </Route>

          <Route element={<AdminRoute />}>
            <Route element={<AdminLayout />}>
              <Route path='/impersonation' element={<ImpersonationPage />} />
              <Route
                path='/app-configuration'
                element={<AppConfigurationPage />}
              />
              <Route path='/resources/create' element={<ResourceCreatePage />} />
            </Route>
          </Route>

          <Route path='/403' element={<ForbiddenPage />} />
          <Route path='*' element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};
